import { getContactLabel } from '@/services/contact-point';
import { SMS_MAX_MESSAGE_LENGTH } from '@bariendo/lib/constants';
import {
  ActionIcon,
  Box,
  Card,
  FileInput,
  Group,
  Loader,
  Menu,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { IconChevronDown, IconDeviceMobileMessage, IconPaperclip, IconSend, IconX } from '@tabler/icons-react';
import commonStyles from '@theme/common.module.css';
import { useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { trackMessageDraftAbandoned } from '@/services/analytics';
import { getOrganizationPhones } from '@/pages/PartnersPage/PartnerTimeline/services/timelineOrganization';
import { FileInputListDisplay } from './FileInputListDisplay';
import { MessageBoxPDFPreview } from './MessageBoxPDFPreview';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { useSendSmsMessage } from '@/hooks/useSendSmsMessage';
import { getPatientName, getPatientPhones } from '@/pages/PatientTimelinePage/services/timelinePatient';

export function SmsMessageBox() {
  const {
    textareaInputRef,
    isSending,
    message,
    setMessage,
    handleSendMessage,
    fileInputRef,
    files,
    handleSelectFiles,
    removeFile,
    handleAttachClick,
    pdfModalOpened,
    handlePdfPreview,
    handleClosePdfModal,
    selectedPdfFile,
    pdfUrl,
    formatFileSize,
    formatPhoneNumber,
  } = useSendSmsMessage();

  const { patientContext, partnerContext, messageBoxContext } = useTimelineContext();

  const patient = patientContext?.patient;
  const organization = partnerContext?.organization;
  const isPartnerContext = !!partnerContext;

  const { selectedPhone, setSelectedPhone, setShowMessageInput } = messageBoxContext;
  const posthog = usePostHog();

  const patientName = useMemo(() => {
    return patient ? getPatientName(patient) : 'Unknown Patient';
  }, [patient]);

  const organizationName = useMemo(() => organization?.name || 'Unknown Partner', [organization]);
  const displayName = isPartnerContext ? organizationName : patientName;

  const patientPhones = useMemo(() => (patient ? getPatientPhones(patient) : []), [patient]);
  const organizationPhones = useMemo(() => getOrganizationPhones(organization), [organization]);
  const phones = isPartnerContext ? organizationPhones : patientPhones;

  const sizeLimitExceeded = message.length > SMS_MAX_MESSAGE_LENGTH;
  const errorMessage = sizeLimitExceeded ? `Message exceeds ${SMS_MAX_MESSAGE_LENGTH} characters` : null;

  return (
    <>
      <Card withBorder padding={0} radius="lg" style={{ backgroundColor: '#ffffff' }}>
        <Box style={{ borderBottom: '1px solid #e9ecef' }}>
          <Group w="100%" p="xs" justify="space-between" align="center">
            <Group>
              <ThemeIcon variant="filled" radius="lg" size="lg" bg="#258195">
                <IconDeviceMobileMessage size={16} />
              </ThemeIcon>
              <Stack gap={0}>
                <Text variant="header">SMS with {displayName}</Text>
                <Group gap="0" align="center">
                  <Text variant="label">
                    {`${formatPhoneNumber(selectedPhone?.value || '')} (${getContactLabel(selectedPhone)})`}
                  </Text>
                  {phones.length > 1 && (
                    <Menu shadow="md" width={200} position="bottom-start">
                      <Menu.Target>
                        <ActionIcon
                          size="md"
                          variant="subtle"
                          color="gray"
                          style={{ minWidth: 'auto', padding: '2px' }}
                        >
                          <IconChevronDown size={12} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown style={{ borderRadius: '8px' }}>
                        <Menu.Label>Choose number</Menu.Label>
                        {phones.map((phone) => (
                          <Menu.Item
                            key={phone.value}
                            onClick={() => setSelectedPhone(phone)}
                            style={{
                              backgroundColor: selectedPhone?.value === phone.value ? '#f8f9fa' : 'transparent',
                            }}
                          >
                            <Stack gap={0} p="sm" style={{ borderRadius: '8px' }} className={commonStyles.hoverable}>
                              <Text variant="label" fw={selectedPhone?.value === phone.value ? 600 : 400}>
                                {getContactLabel(phone)}
                              </Text>
                              <Text variant="caption">{formatPhoneNumber(phone.value || '')}</Text>
                            </Stack>
                          </Menu.Item>
                        ))}
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Group>
              </Stack>
            </Group>
            <Tooltip label="Close" withArrow position="top-end" openDelay={500}>
              <ActionIcon
                variant="subtle"
                size="md"
                radius="xl"
                aria-label="Close"
                onClick={() => {
                  if (message.trim()) {
                    trackMessageDraftAbandoned(posthog, {
                      message_type: 'sms',
                      draft_length: message.length,
                      had_attachments: files.length > 0,
                      patient_id: patient?.id,
                      organization_id: organization?.id,
                    });
                  }
                  setShowMessageInput(false);
                }}
                className={commonStyles.hover}
              >
                <IconX size={16} color="#6c757d" />
              </ActionIcon>
            </Tooltip>
          </Group>

          {/* Content */}
          <Box p="lg" style={{ borderTop: '1px solid #e9ecef' }}>
            <Group gap="xs">
              <Textarea
                ref={textareaInputRef}
                disabled={isSending}
                placeholder="Write a message..."
                flex="1"
                styles={{ input: sizeLimitExceeded ? { borderColor: 'red' } : undefined }}
                radius="md"
                onChange={(event) => setMessage(event.currentTarget.value)}
                error={errorMessage}
                value={message}
                onKeyDown={(event) => {
                  // An IME commits its candidate with Enter; that keystroke is not a send.
                  if (event.nativeEvent.isComposing) {
                    return;
                  }

                  const isPrintable = event.key.length === 1 && !event.ctrlKey && !event.metaKey;

                  // Blocks enter key if size limit is exceeded
                  if (sizeLimitExceeded && event.key === 'Enter') {
                    event.preventDefault();
                  }

                  // Block typing if already at limit
                  if (message.length >= SMS_MAX_MESSAGE_LENGTH && isPrintable) {
                    event.preventDefault();
                  }

                  if (
                    event.key === 'Enter' &&
                    !event.shiftKey &&
                    !sizeLimitExceeded &&
                    (message.length > 0 || files.length > 0)
                  ) {
                    event.preventDefault();
                    if (!selectedPhone) return;
                    handleSendMessage(selectedPhone);
                  }
                }}
                autosize
                maxRows={10}
                minRows={2}
                resize="vertical"
              />
              {/* Hidden FileInput that can be triggered programmatically */}
              <FileInput
                accept="application/pdf,image/*"
                multiple
                style={{ display: 'none' }}
                ref={fileInputRef}
                value={files}
                onChange={handleSelectFiles}
                disabled={isSending}
              />
              <Tooltip label="Attach a file" withArrow position="top-end" openDelay={500}>
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  radius="xl"
                  aria-label="Attach"
                  onClick={handleAttachClick}
                  disabled={isSending}
                  className={commonStyles.hover}
                >
                  <IconPaperclip size={20} color="#212529" />
                </ActionIcon>
              </Tooltip>
              <Tooltip
                label={
                  sizeLimitExceeded
                    ? 'Message size limit exceeded'
                    : message.length > 0 || files.length > 0
                      ? 'Send message'
                      : 'Add a message or attachment'
                }
                withArrow
                position="top-end"
                openDelay={400}
              >
                <ActionIcon
                  variant="filled"
                  size="lg"
                  radius="xl"
                  color="#246A7A"
                  aria-label="Send"
                  onClick={() => handleSendMessage(selectedPhone!)}
                  disabled={(message.length === 0 && files.length === 0) || !selectedPhone || sizeLimitExceeded}
                >
                  {isSending ? <Loader size={16} color="white" /> : <IconSend size={20} />}
                </ActionIcon>
              </Tooltip>
            </Group>
            {/* Attachment display */}
            <FileInputListDisplay
              files={files}
              formatFileSize={formatFileSize}
              handlePdfPreview={handlePdfPreview}
              removeFile={removeFile}
            />
          </Box>
        </Box>
      </Card>

      {/* PDF Preview Modal */}
      <MessageBoxPDFPreview
        opened={pdfModalOpened}
        onClose={handleClosePdfModal}
        file={selectedPdfFile}
        pdfUrl={pdfUrl}
      />
    </>
  );
}
